import type { SessionIdentity } from "./Platform";

/**
 * The organization slug in the cache that `useCompatOrganization` writes for the
 * bearer-token embed flow. This is a fallback. A host reads its own session source
 * first. Both cache shapes are accepted: the flat `/session/me` organization and
 * the wrapped `{ organization }` record.
 */
import {
    currentTenantContentScopeKey,
    currentTenantScope,
    isTenantScopeRequired,
} from "../contexts/tenantScopeHeaders";

/**
 * The durable-storage namespace a canonical company/project session imposes.
 *
 * One Stytch organization can serve several projects, so the organization slug
 * is not a safe key for anything that outlives a page: the caches would collide
 * across projects. Under a canonical URL the validated content scope key names
 * the tenant instead. `undefined` means no canonical scope is in play and the
 * caller should fall back to its own organization slug; `null` means a canonical
 * scope is required but not yet validated, so nothing may be cached at all.
 * (PP-827)
 */
export function canonicalScopeOrgSlug(): string | null | undefined {
    if (!isTenantScopeRequired()) return undefined;
    const key = currentTenantContentScopeKey();
    return key ? `scope:${encodeURIComponent(key)}` : null;
}

/** An adopted project names no organization; its project key names the project. */
const ADOPTED_PROJECT_KEY_PREFIX = "project:";

/**
 * The project key: the name of the local state that belongs to a project rather than to
 * an authority over it, so publishing a URL or changing a role leaves it alone. Display
 * preferences are keyed by it alone, personal history by it plus the admitted person.
 * Server content stays on `canonicalScopeOrgSlug`, which rotates with the authority.
 *
 * Under a canonical URL scope it is the admitted project's `storage_org_slug`, the slug
 * the project's legacy tabs use, so both tabs read one key; an adopted project, for which
 * the server names none, gets `project:<project_id>`. Null while a canonical scope is
 * required but not validated. Otherwise the caller's legacy organization slug.
 */
export function localScopeSlug(legacyOrgSlug: string | null | undefined): string | null {
    const scoped = canonicalScopeOrgSlug();
    if (scoped === undefined) return legacyOrgSlug || null;
    const scope = scoped ? currentTenantScope() : null;
    if (!scope) return null;
    const slug = scope.storageOrgSlug;
    return slug && !slug.startsWith("scope:")
        ? slug
        : `${ADOPTED_PROJECT_KEY_PREFIX}${scope.projectId}`;
}

/**
 * The tenant slug to send a server or third party as the organization (first-party
 * product events, Amplitude): the project key, except that an adopted project's has no
 * organization in it, so it is null. Never a `scope:` storage key.
 */
export function selectedTenantOrgSlug(legacyOrgSlug: string | null | undefined): string | null {
    const slug = localScopeSlug(legacyOrgSlug);
    return slug?.startsWith(ADOPTED_PROJECT_KEY_PREFIX) ? null : slug;
}

export function embedOrganizationSlug(): string | null {
    try {
        const raw = localStorage.getItem("embed_organization_cache");
        if (!raw) return null;
        const parsed: {
            organization_slug?: string;
            organization?: { organization_slug?: string };
        } = JSON.parse(raw);
        return parsed.organization_slug ?? parsed.organization?.organization_slug ?? null;
    } catch {
        return null;
    }
}

/** The claim Stytch's B2B session JWT carries the organization under. */
const STYTCH_ORGANIZATION_CLAIM = "https://stytch.com/organization";

/**
 * Reads the organization and member out of a Stytch B2B session JWT: `sub` is the
 * member id, the organization claim carries `organization_id` and `slug`. Under a
 * native session (the desktop, an embed) the JWT is in hand as soon as the host's
 * session probe answers, one network round trip before `/api/ai/session/me` -- and the
 * tenant client must not wait for that read: a client created before the identity
 * is known rotates when it lands, remounting the window and refetching everything.
 * The claims are read, not verified: they only name a cache scope, and the same
 * token authenticates every request whose data lands under it. `null` for a token
 * that is not a JWT or lacks either id.
 */
export function sessionIdentityFromJwt(
    sessionJwt: string | null | undefined,
): SessionIdentity | null {
    const payload = sessionJwt?.split(".")[1];
    if (!payload) return null;
    try {
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const claims: unknown = JSON.parse(
            atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")),
        );
        if (typeof claims !== "object" || claims === null) return null;
        const memberId = (claims as Record<string, unknown>).sub;
        const organization = (claims as Record<string, unknown>)[STYTCH_ORGANIZATION_CLAIM];
        if (typeof organization !== "object" || organization === null) return null;
        const {
            organization_id: organizationId,
            slug,
            organization_slug: legacySlug,
        } = organization as Record<string, unknown>;
        const organizationSlug = typeof slug === "string" && slug.length > 0 ? slug : legacySlug;
        if (
            typeof memberId !== "string" ||
            memberId.length === 0 ||
            typeof organizationId !== "string" ||
            organizationId.length === 0 ||
            typeof organizationSlug !== "string" ||
            organizationSlug.length === 0
        ) {
            return null;
        }
        return { organizationId, organizationSlug, memberId };
    } catch {
        return null;
    }
}
